import { registerActions } from "../actionRegistry";
/**
 * modal.ts — Imperative bridge to the Preact ModalStack.
 *
 * All public function signatures are identical to the pre-refactor API so
 * that callers (16+ TS files, actionDelegator, PHP data-action attributes,
 * window.modal() etc.) continue to work without changes.
 *
 * The actual modal lifecycle is managed by ModalStack.tsx via Preact state.
 * This file merely delegates to the ModalStack handle.
 */
import { getDomElement, addClass, removeClass } from "../utils/dom";
import { setCookie } from "../features/cookies";
import { selectCounter } from "../features/selection";
import { docollmove } from "../features/collections";
import { reordermode, reorderSelectedCount } from "../globalState";
import { getModalStackHandle, isModalLocked } from "./ModalStack";

// ── Escape-key listener (registered once via initModal) ──────

export function initModal(): void {
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      // A locked modal owns its own dismissal — see setModalLocked in ModalStack.
      if (isModalLocked()) return;
      const handle = getModalStackHandle();
      if (handle && handle.getDepth() > 0) {
        handle.closeLast();
      }
    }
  });
}

// ── Core modal operations ────────────────────────────────────

export function modal(what: string, size = "small", swap = 0, pushHistory = false): void {
  const handle = getModalStackHandle();
  if (handle) handle.open(what, size, swap, pushHistory);
}

/**
 * Swap the open artwork modal for another artwork, leaving a history entry behind.
 *
 * Moving between artworks — the prev/next arrows, a thumbnail in "More like this" — is
 * navigation, and the reader expects Back to return to the artwork they came from. A plain
 * swap replaces the address-bar entry instead, so Back from the third related image landed
 * on whatever page the first was opened from (a collection, a search) and everything in
 * between was gone.
 *
 * `urlcheck` (features/navigation.ts) already turns the resulting Back/Forward into a swap,
 * so pushing here is the whole fix; it deliberately does NOT push, because the URL it is
 * reacting to is one the browser has already restored.
 */
export function swapArtworkModal(what: string): void {
  modal(what, "full", 1, true);
}

export function closeModalLast(): void {
  const handle = getModalStackHandle();
  if (handle) handle.closeLast();
}

export function closeModal(modalId: string): void {
  const handle = getModalStackHandle();
  if (handle) handle.close(modalId);
}

export function refreshModal(): void {
  const handle = getModalStackHandle();
  if (handle) handle.refresh();
}

export function closeModalLastRefresh(): void {
  const handle = getModalStackHandle();
  if (handle) handle.closeLastRefresh();
}

// ── Layout toggles (operate on DOM inside modal content) ─────

export function modalLayout(layout: "list" | "thumbs"): void {
  if (layout !== "list" && layout !== "thumbs") return;

  setCookie("PictufyModalLayout", layout, 100000);

  const listBtn = getDomElement("modallayout-list");
  const thumbsBtn = getDomElement("modallayout-thumbs");
  const artworksContainer = getDomElement("directory-artworks");

  if (layout === "list") {
    if (listBtn) {
      addClass(listBtn, "darkgrey");
      removeClass(listBtn, "grey");
    }
    if (thumbsBtn) {
      addClass(thumbsBtn, "grey");
      removeClass(thumbsBtn, "darkgrey");
    }
    if (artworksContainer) {
      removeClass(artworksContainer, "directory-artworks-thumbs");
      addClass(artworksContainer, "directory-artworks-list");
    }

    document.querySelectorAll(".directory-artwork").forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.style.display === "inline-block") {
        htmlEl.style.display = "block";
      }
    });
  }

  if (layout === "thumbs") {
    if (thumbsBtn) {
      addClass(thumbsBtn, "darkgrey");
      removeClass(thumbsBtn, "grey");
    }
    if (listBtn) {
      addClass(listBtn, "grey");
      removeClass(listBtn, "darkgrey");
    }
    if (artworksContainer) {
      removeClass(artworksContainer, "directory-artworks-list");
      addClass(artworksContainer, "directory-artworks-thumbs");
    }

    document.querySelectorAll(".directory-artwork").forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.style.display === "block") {
        htmlEl.style.display = "inline-block";
      }
    });
  }
}

export function modalLayoutAlt(layout: "list" | "thumbs"): void {
  if (layout !== "list" && layout !== "thumbs") return;

  setCookie("PictufyModalLayout", layout, 100000);

  const listBtn = getDomElement("modallayout-listalt");
  const thumbsBtn = getDomElement("modallayout-thumbsalt");
  const artworksContainer = getDomElement("directory-artworksalt");

  if (layout === "list") {
    if (listBtn) {
      addClass(listBtn, "darkgrey");
      removeClass(listBtn, "grey");
    }
    if (thumbsBtn) {
      addClass(thumbsBtn, "grey");
      removeClass(thumbsBtn, "darkgrey");
    }
    if (artworksContainer) {
      removeClass(artworksContainer, "directory-artworks-thumbs");
      addClass(artworksContainer, "directory-artworks-list");
    }
  }

  if (layout === "thumbs") {
    if (thumbsBtn) {
      addClass(thumbsBtn, "darkgrey");
      removeClass(thumbsBtn, "grey");
    }
    if (listBtn) {
      addClass(listBtn, "grey");
      removeClass(listBtn, "darkgrey");
    }
    if (artworksContainer) {
      removeClass(artworksContainer, "directory-artworks-list");
      addClass(artworksContainer, "directory-artworks-thumbs");
    }
  }
}

// ── picModal (selection / reorder aware) ─────────────────────

export function picModal(what: string, layout: string, id: string, event: MouseEvent): void {
  if (!(event.target instanceof HTMLImageElement)) return;

  if (reordermode === 0) {
    const selectEl = getDomElement<HTMLInputElement>("lm_select");
    if (selectEl?.value === "1") {
      const selectedInput = getDomElement<HTMLInputElement>(`selected-${id}`);
      const selectIcon = getDomElement(`select-icon-${id}`);

      if (selectedInput && selectIcon) {
        if (selectedInput.value === "1") {
          addClass(selectIcon, "far");
          addClass(selectIcon, "fa-square");
          removeClass(selectIcon, "fas");
          removeClass(selectIcon, "fa-check-square");
          selectedInput.value = "0";
        } else {
          removeClass(selectIcon, "far");
          removeClass(selectIcon, "fa-square");
          addClass(selectIcon, "fas");
          addClass(selectIcon, "fa-check-square");
          selectedInput.value = "1";
        }
        selectCounter();
      }
    } else {
      modal(what, layout);
    }
  } else {
    if (reorderSelectedCount > 0) {
      docollmove(id);
    }
  }
}

// ── URL update (public helper) ───────────────────────────────

/**
 * `replace` was a `number` compared against `1`, and this was registered as a
 * dispatchable action. Anything arriving through `data-args` is a **string**, so
 * a `data-fn="urlupdate" data-args="…,1"` would have taken the pushState branch
 * with `replace === "1"`. Nothing ever dispatched it — the sole caller imports
 * the function directly — so the registration is gone and the flag is a boolean,
 * which has no string form to be wrong about.
 */
export function urlUpdatePublic(url: string, replace: boolean): void {
  if (replace) {
    window.history.replaceState(null, "", url);
  } else {
    window.history.pushState(null, "", url);
  }
}

// ── Legacy aliases for backward compatibility ────────────────

export { closeModalLast as closemodallast };
export { closeModalLastRefresh as closemodallastrefresh };
export { closeModal as closemodal };
export { refreshModal as refreshmodal };
export { modalLayout as modallayout };
export { modalLayoutAlt as modallayoutalt };
export { picModal as picmodal };

registerActions({
  closemodal: closeModal,
  closemodallast: closeModalLast,
  closemodallastrefresh: closeModalLastRefresh,
  refreshmodal: refreshModal,
  modallayout: modalLayout,
  modallayoutalt: modalLayoutAlt,
  picmodal: picModal,
});
